// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — Login Page (Mind-blowing entry)
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { setAuth } from '@/lib/store';
import { Button, Input } from '@/components/ui';
import { Sparkles, Shield, Zap, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result =
        mode === 'login'
          ? await api.login(email, password)
          : await api.register(email, name, password);

      setAuth(result.user, result.token);
      toast.success(t('auth.welcomeToast', { name: result.user.name }));
      navigate('/projects');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('auth.authFailed'));
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Zap, text: t('loginFeatures.aiTitle'), desc: t('loginFeatures.aiDesc') },
    { icon: Shield, text: t('loginFeatures.roleTitle'), desc: t('loginFeatures.roleDesc') },
    { icon: Sparkles, text: t('loginFeatures.visualTitle'), desc: t('loginFeatures.visualDesc') },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left — Background with animation */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-surface-950">
        {/* Animated gradient orbs */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-600/20 rounded-full blur-3xl animate-pulse animation-delay-1000" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-brand-500/10 rounded-full blur-3xl animate-pulse animation-delay-2000" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <img
              src="/arams-logo-white.png"
              alt={t('common.appName')}
              className="w-24 h-24 rounded-3xl mx-auto mb-8 shadow-2xl shadow-brand-500/30 object-contain"
            />
            <h1 className="text-5xl font-extrabold mb-4">
              <span className="gradient-text">{t('common.appName')}</span>
            </h1>
            <p className="text-xl text-surface-400 mb-12">{t('common.appFullName')}</p>

            {/* Feature highlights */}
            <div className="space-y-6 max-w-sm mx-auto text-left">
              {features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + i * 0.2 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-10 h-10 rounded-xl bg-brand-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-brand-400" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-surface-200">{feature.text}</div>
                    <div className="text-xs text-surface-500">{feature.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right — Login form */}
      <div className="flex-1 lg:max-w-xl flex items-center justify-center p-8 bg-surface-900/50 backdrop-blur-xl">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img
              src="/arams-logo-white.png"
              alt={t('common.appName')}
              className="w-10 h-10 rounded-xl object-contain"
            />
            <span className="font-bold text-xl gradient-text">{t('common.appName')}</span>
          </div>

          <h2 className="text-2xl font-bold text-surface-100 mb-2">
            {mode === 'login' ? t('auth.welcomeBack') : t('auth.createAccount')}
          </h2>
          <p className="text-surface-400 mb-8">
            {mode === 'login' ? t('auth.signInSubtitle') : t('auth.registerSubtitle')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === 'register' && (
              <Input
                label={t('auth.fullName')}
                type="text"
                placeholder={t('auth.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            )}
            <Input
              label={t('auth.email')}
              type="email"
              placeholder={t('auth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              label={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              placeholder={t('auth.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-surface-400 hover:text-surface-200 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              }
            />

            {mode === 'login' && (
              <div className="flex justify-end -mt-2">
                <Link
                  to="/forgot-password"
                  className="text-xs text-surface-400 hover:text-brand-400 transition-colors"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>
            )}

            <Button type="submit" loading={loading} className="w-full">
              {mode === 'login' ? t('auth.signIn') : t('auth.signUp')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Button variant="link" onClick={() => setMode(mode === 'login' ? 'register' : 'login')}>
              {mode === 'login' ? t('auth.switchToRegister') : t('auth.switchToLogin')}
            </Button>
          </div>

          <div className="mt-12 text-center">
            <p className="text-xs text-surface-600">
              {t('common.copyright', { year: new Date().getFullYear() })}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
